// ─── Multi-comptes : bascule « sans ressaisie » (style Teams/WhatsApp) ────────
// Porté de MoPCal (services/comptes.js), version e-mail/mot de passe.
//
// Principe (AUCUN mot de passe stocké) :
//   • Chaque compte connu vit dans SA propre FirebaseApp « cpt_<uid> » avec sa
//     session persistée (localStorage web / AsyncStorage natif) → Firebase garde
//     plusieurs sessions valides en parallèle, isolées.
//   • L'app PRIMAIRE ne fait que refléter le compte actif : on y recopie la
//     session cible via updateCurrentUser (qui déclenche onAuthStateChanged →
//     AuthContext se met à jour, sans recharger l'app).
//
// La liste des comptes (uid/email/prénom/couleur) est stockée localement pour
// peupler le sélecteur ; les sessions sont gérées par Firebase.
import { initializeApp, getApp } from 'firebase/app';
import {
  getAuth, initializeAuth, browserLocalPersistence, getReactNativePersistence,
  updateCurrentUser, signInWithEmailAndPassword, signOut,
} from 'firebase/auth';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Platform } from 'react-native';
import { auth as authPrincipal, firebaseConfig } from '../firebase';

const KEY = 'mop_comptes_v1';

function authDe(uid) {
  const nom = 'cpt_' + uid;
  let app;
  try { app = getApp(nom); } catch { app = initializeApp(firebaseConfig, nom); }
  try {
    return initializeAuth(app, {
      persistence: Platform.OS === 'web' ? browserLocalPersistence : getReactNativePersistence(AsyncStorage),
    });
  } catch { return getAuth(app); }
}

async function pretAuth(a) {
  try { if (a.authStateReady) { await a.authStateReady(); return; } } catch {}
  for (let i = 0; i < 20 && !a.currentUser; i++) await new Promise(r => setTimeout(r, 100));
}

// ── Liste locale ──────────────────────────────────────────────────
export async function listeComptes() {
  try { return JSON.parse(await AsyncStorage.getItem(KEY)) || []; } catch { return []; }
}
async function ecrireComptes(l) { try { await AsyncStorage.setItem(KEY, JSON.stringify(l)); } catch {} }
async function ajouterALaListe({ uid, email, prenom, couleur }) {
  if (!uid) return;
  const l = await listeComptes();
  const exist = l.find(c => c.uid === uid);
  const sans = l.filter(c => c.uid !== uid);
  sans.push({
    uid,
    email:   email || exist?.email || '',
    prenom:  prenom || exist?.prenom || '',
    couleur: couleur != null ? couleur : (exist?.couleur ?? null),
  });
  await ecrireComptes(sans);
}

export function compteActifUid() {
  try { return authPrincipal.currentUser?.uid || null; } catch { return null; }
}

// Mémorise le compte actuellement connecté (recopie sa session dans son app
// dédiée, sans mot de passe) et l'ajoute à la liste.
export async function memoriserCompteCourant(infos = {}) {
  const u = authPrincipal.currentUser;
  if (!u) return;
  try { await updateCurrentUser(authDe(u.uid), u); } catch {}
  await ajouterALaListe({ uid: u.uid, email: infos.email || u.email, prenom: infos.prenom || '', couleur: infos.couleur || null });
}

export async function majInfosCompte(uid, { prenom, couleur } = {}) {
  const l = await listeComptes();
  const i = l.findIndex(c => c.uid === uid);
  if (i < 0) return;
  if (prenom !== undefined) l[i].prenom = prenom || '';
  if (couleur !== undefined) l[i].couleur = couleur || null;
  await ecrireComptes(l);
}

// Bascule vers un compte mémorisé (aucune ressaisie). onAuthStateChanged de
// l'AuthContext reflète le changement sans recharger l'app.
export async function basculerVers(uid) {
  if (uid === compteActifUid()) return;
  try { const c = authPrincipal.currentUser; if (c) await updateCurrentUser(authDe(c.uid), c); } catch {}
  const cible = authDe(uid);
  await pretAuth(cible);
  const u = cible.currentUser;
  if (!u) throw new Error('SESSION_EXPIREE');
  await updateCurrentUser(authPrincipal, u);
}

// Ajoute un compte par e-mail / mot de passe (une saisie), le rend actif.
export async function ajouterCompteEmail(email, motDePasse) {
  const mail = (email || '').trim().toLowerCase();
  if (!mail || !motDePasse) throw new Error('E-mail et mot de passe requis.');
  await memoriserCompteCourant();
  try {
    const cred = await signInWithEmailAndPassword(authPrincipal, mail, motDePasse);
    const u = cred.user;
    try { await updateCurrentUser(authDe(u.uid), u); } catch {}
    await ajouterALaListe({ uid: u.uid, email: u.email });
  } catch (e) {
    const code = e?.code || '';
    if (code === 'auth/invalid-credential' || code === 'auth/wrong-password' || code === 'auth/user-not-found')
      throw new Error('E-mail ou mot de passe incorrect.');
    if (code === 'auth/too-many-requests') throw new Error('Trop de tentatives. Réessayez plus tard.');
    throw new Error(e?.message || 'Connexion impossible.');
  }
}

// Retire un compte. Si c'est l'actif : bascule vers un autre, sinon 'DECONNEXION'.
export async function retirerCompte(uid) {
  const actif = compteActifUid();
  try { await signOut(authDe(uid)); } catch {}
  const restants = (await listeComptes()).filter(c => c.uid !== uid);
  await ecrireComptes(restants);
  if (uid === actif) {
    if (restants.length > 0) { await basculerVers(restants[0].uid); return 'BASCULE'; }
    return 'DECONNEXION';
  }
  return 'RETIRE';
}
